// VoiceFabric Rule Builder page — slim orchestrator. Each tab and the
// header strip live in /pages/admin/voicefabric/.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, Activity, Webhook, FileText, Link2, Radio,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { IntegrationsTab } from './voicefabric/IntegrationsTab';
import { RuleTemplatesModal } from './voicefabric/RuleTemplatesModal';
import { RuleMetricsModal } from './voicefabric/RuleMetricsModal';
import { RuleEditor } from './voicefabric/RuleEditor';
import { ActionsLog } from './voicefabric/ActionsLog';
import { RecentActivityPanel } from './voicefabric/RecentActivityPanel';
import { VoiceFabricHeader, VoiceFabricTabs } from './voicefabric/VoiceFabricHeader';
import { RulesTab } from './voicefabric/RulesTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_RULE = {
  name: '',
  description: '',
  trigger_prompt: '',
  action_type: 'log',
  action_config: { extract_fields: ['intent_summary'], webhook_url: '', connection_id: '' },
  enabled: true,
};

const BASE_ACTION_LABELS = {
  log: { label: 'Log Only', icon: FileText, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  webhook: { label: 'Webhook POST', icon: Webhook, color: 'text-violet-400 bg-violet-500/10 border-violet-500/20' },
};

const TAB_DEFS = [
  { id: 'rules', label: 'Rules', icon: Zap },
  { id: 'activity', label: 'Live Activity', icon: Radio },
  { id: 'actions', label: 'Actions Log', icon: Activity },
  { id: 'integrations', label: 'Integrations', icon: Link2 },
];

const VoiceFabricPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState('');
  const [tab, setTab] = useState('rules');
  const [rules, setRules] = useState([]);
  const [actions, setActions] = useState([]);
  const [connections, setConnections] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [loadingActions, setLoadingActions] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [metricsRule, setMetricsRule] = useState(null);

  // Resolver used by rule cards for the action pill label/icon
  const actionLabelFor = useCallback((actionType) => {
    if (BASE_ACTION_LABELS[actionType]) return BASE_ACTION_LABELS[actionType];
    const spec = integrations.find(i => i.id === actionType);
    if (!spec) return BASE_ACTION_LABELS.log;
    return {
      label: spec.label,
      icon: Link2,
      color: 'text-teal-400 bg-teal-500/10 border-teal-500/20',
    };
  }, [integrations]);

  // Load domains + integration catalog on mount
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/domains`, { headers }),
      axios.get(`${API}/admin/voicefabric/integrations`, { headers }),
    ]).then(([dRes, iRes]) => {
      setDomains(dRes.data);
      setIntegrations(iRes.data.integrations || []);
      if (dRes.data.length) setDomainId(d => d || dRes.data[0].id);
    }).catch(() => toast.error('Failed to load domains/integrations'));
  }, [headers]);

  const loadRules = useCallback(async () => {
    if (!domainId) return;
    setLoadingRules(true);
    try {
      const r = await axios.get(`${API}/admin/domains/${domainId}/voicefabric/rules`, { headers });
      setRules(r.data.rules || []);
    } catch {
      toast.error('Failed to load rules');
    } finally {
      setLoadingRules(false);
    }
  }, [domainId, headers]);

  const loadActions = useCallback(async () => {
    if (!domainId) return;
    setLoadingActions(true);
    try {
      const r = await axios.get(`${API}/admin/domains/${domainId}/voicefabric/actions?limit=100`, { headers });
      setActions(r.data.actions || []);
    } catch {
      toast.error('Failed to load actions');
    } finally {
      setLoadingActions(false);
    }
  }, [domainId, headers]);

  const loadConnections = useCallback(async () => {
    if (!domainId) return;
    try {
      const r = await axios.get(`${API}/admin/domains/${domainId}/voicefabric/connections`, { headers });
      setConnections(r.data.connections || []);
    } catch (err) {
      // Not fatal — rule editor just won't show connection options.
      console.warn('VoiceFabric: loadConnections failed', err);
    }
  }, [domainId, headers]);

  useEffect(() => {
    loadConnections();
    if (tab === 'rules') loadRules();
    if (tab === 'actions') { loadRules(); loadActions(); }
  }, [tab, loadRules, loadActions, loadConnections]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleSave = async (form) => {
    if (!domainId) return;
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/admin/domains/${domainId}/voicefabric/rules/${editing.id}`, form, { headers });
        toast.success('Rule updated');
      } else {
        await axios.post(`${API}/admin/domains/${domainId}/voicefabric/rules`, form, { headers });
        toast.success('Rule created');
      }
      setEditing(null);
      loadRules();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
        : 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? Past actions will be preserved.`)) return;
    try {
      await axios.delete(`${API}/admin/domains/${domainId}/voicefabric/rules/${rule.id}`, { headers });
      toast.success('Rule deleted');
      loadRules();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleToggle = async (rule) => {
    try {
      await axios.put(`${API}/admin/domains/${domainId}/voicefabric/rules/${rule.id}`,
        { enabled: !rule.enabled }, { headers });
      loadRules();
    } catch {
      toast.error('Toggle failed');
    }
  };

  const handleTest = async (ruleId, transcript) => {
    const r = await axios.post(`${API}/admin/domains/${domainId}/voicefabric/rules/${ruleId}/test`,
      { transcript }, { headers });
    return r.data;
  };

  const handleFeedback = async (actionId, feedback) => {
    try {
      await axios.post(`${API}/admin/voicefabric/actions/${actionId}/feedback`,
        { feedback }, { headers });
      setActions(list => list.map(a =>
        a.id === actionId ? { ...a, feedback: feedback === 'clear' ? null : feedback } : a
      ));
    } catch {
      toast.error('Failed to record feedback');
    }
  };

  // "Tune rule from action" — open the rule editor pre-filled with a
  // "DO NOT MATCH" example built from this false-positive action.
  const handleTuneRule = (action) => {
    const rule = rules.find(r => r.id === action.rule_id);
    if (!rule) {
      toast.error('Original rule no longer exists — cannot tune.');
      return;
    }
    const snippet = (action.snippet || '').trim();
    const reason = (action.reason || '').trim();
    const tuningBlock = [
      '',
      '',
      '--- FALSE POSITIVE — DO NOT match transcripts like this ---',
      reason ? `The rule wrongly matched with reason: "${reason}".` : null,
      snippet ? `Do NOT match transcripts containing text similar to:\n"${snippet}"` : null,
      `Call reviewed: ${action.call_id || 'unknown'}`,
    ].filter(Boolean).join('\n');

    // Guard against re-appending the same block twice.
    const alreadyTuned = rule.trigger_prompt?.includes(`Call reviewed: ${action.call_id}`);
    const nextPrompt = alreadyTuned
      ? rule.trigger_prompt
      : `${rule.trigger_prompt || ''}${tuningBlock}`;

    setEditing({ ...rule, trigger_prompt: nextPrompt });
    toast.info('Rule editor pre-filled with the false-positive as a negative example. Review and save.');
  };

  const existingTemplateIds = useMemo(
    () => rules.map(r => r.template_id).filter(Boolean),
    [rules],
  );

  const activeDomain = domains.find(d => d.id === domainId);

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl">
        <VoiceFabricHeader
          domains={domains}
          domainId={domainId}
          onDomainChange={setDomainId}
          activeDomain={activeDomain}
        />
        <VoiceFabricTabs tabs={TAB_DEFS} current={tab} onChange={setTab} />

        {tab === 'rules' && (
          <RulesTab
            rules={rules}
            loading={loadingRules}
            actionLabelFor={actionLabelFor}
            domainId={domainId}
            onOpenTemplates={() => setShowTemplates(true)}
            onNewRule={() => setEditing({ ...EMPTY_RULE })}
            onMetrics={setMetricsRule}
            onToggle={handleToggle}
            onEdit={setEditing}
            onDelete={handleDelete}
          />
        )}

        {tab === 'activity' && <RecentActivityPanel token={token} domainId={domainId} />}
        {tab === 'actions' && (
          <ActionsLog actions={actions} loading={loadingActions} rules={rules} onFeedback={handleFeedback} onTuneRule={handleTuneRule} />
        )}
        {tab === 'integrations' && <IntegrationsTab domainId={domainId} token={token} />}

        {editing && (
          <RuleEditor
            initial={editing}
            onClose={() => setEditing(null)}
            onSave={handleSave}
            saving={saving}
            onTest={handleTest}
            connections={connections}
            integrations={integrations}
            token={token}
          />
        )}
        {metricsRule && (
          <RuleMetricsModal
            rule={metricsRule}
            domainId={domainId}
            token={token}
            onClose={() => setMetricsRule(null)}
          />
        )}
        {showTemplates && (
          <RuleTemplatesModal
            domainId={domainId}
            token={token}
            existingTemplateIds={existingTemplateIds}
            onClose={() => setShowTemplates(false)}
            onSpawned={() => loadRules()}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default VoiceFabricPage;
